import { trackById } from 'src/app/shared/utils/track-by.util';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SelectPanelWidthDirective } from '../../../../_helpers/select-panel-width.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, switchMap, catchError, map } from 'rxjs/operators';
import { Lookup } from 'src/app/models/lookup.model';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { AgentService } from 'src/app/services/agent.service';
import { SegService } from 'src/app/services/seg.service';
import { SampleSurveyTypeService } from 'src/app/services/sample-survey-type.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { SampleMetricsGranularity, SampleMetricsTimeRange } from 'src/app/constants/sample-metrics.constants';
import {
    SampleCollectionMetricsFilterValues,
    SAMPLE_METRICS_FILTER_DEFAULTS,
    STACK_BY_OPTIONS,
    GRANULARITY_OPTIONS,
    SHIFT_TYPE_OPTIONS,
    TIME_RANGE_OPTIONS,
    resolveTimeRangeDates,
    finestGranularityFor,
    clampGranularity
} from './sample-collection-metrics-widget.constants';
import { SearchableList } from '../_shared/searchable-list';
import { CustomDateRangeController, TimePeriodOptionWithRange } from '../_shared/custom-date-range-controller';
import nameOf from 'src/app/shared/form/nameOf.helper';

interface GroupCascadeTrigger {
    groupId: string | null;
    userDriven: boolean;
}

@Component({
    standalone: true,
    selector: 'app-sample-collection-metrics-filters',
    imports: [
    FormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    NgxMatSelectSearchModule,
    SelectPanelWidthDirective
],
    templateUrl: './sample-collection-metrics-filters.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./sample-collection-metrics-filters.component.scss']
})
export class SampleCollectionMetricsFiltersComponent implements OnInit, OnChanges, OnDestroy {
    @Input() mode: 'inline' | 'config' = 'inline';
    @Input() values: SampleCollectionMetricsFilterValues = { ...SAMPLE_METRICS_FILTER_DEFAULTS };
    @Output() filterChange = new EventEmitter<SampleCollectionMetricsFilterValues>();
    @Output() filterReconcile = new EventEmitter<SampleCollectionMetricsFilterValues>();

    agentGroups = new SearchableList<Lookup>((g, q) => !!g.name?.toLowerCase().includes(q));
    agents = new SearchableList<Lookup>(
        (a, q) => !!a.name?.toLowerCase().includes(q),
        () => this.values.selectedAgentIds,
    );
    segs = new SearchableList<Lookup>(
        (s, q) => !!s.name?.toLowerCase().includes(q),
        () => this.values.selectedSegIds,
    );
    surveyTypes = new SearchableList<Lookup>(
        (t, q) => !!t.name?.toLowerCase().includes(q),
        () => this.values.selectedSurveyTypeIds,
    );

    stackByOptions = STACK_BY_OPTIONS;
    granularityOptions = GRANULARITY_OPTIONS;
    shiftTypeOptions = SHIFT_TYPE_OPTIONS;
    timeRangeOptionsWithRange: TimePeriodOptionWithRange[] = [];
    granularityFloor: SampleMetricsGranularity = SampleMetricsGranularity.Monthly;
    readonly SampleMetricsTimeRange = SampleMetricsTimeRange;

    private destroy$ = new Subject<void>();
    private readonly groupChange$ = new Subject<GroupCascadeTrigger>();
    private readonly agentGroupField = nameOf<SampleCollectionMetricsFilterValues>(x => x.selectedAgentGroupId);
    private dateRangeCtrl: CustomDateRangeController;

    get customTimeRangeLabel(): string {
        return this.dateRangeCtrl.formatLabel(this.values.customStartDate, this.values.customEndDate);
    }

    constructor(
        private agentGroupService: AgentGroupService,
        private agentService: AgentService,
        private segService: SegService,
        private sampleSurveyTypeService: SampleSurveyTypeService,
        dateFormatService: DateFormatService,
        private translate: TranslateService,
        dialog: MatDialog
    ) {
        this.dateRangeCtrl = new CustomDateRangeController(dialog, dateFormatService, translate);
    }

    ngOnInit(): void {
        this.timeRangeOptionsWithRange = this.dateRangeCtrl.buildOptionsWithRanges(
            TIME_RANGE_OPTIONS,
            SampleMetricsTimeRange.Custom,
            resolveTimeRangeDates,
        );
        this.dateRangeCtrl.syncFromStrings(this.values.customStartDate, this.values.customEndDate);
        this.values = { ...this.values, selectedGranularity: this.clampSelectedGranularity() };
        this.refreshGranularityFloor();
        this.subscribeGroupCascade();
        forkJoin({
            agentGroups: this.agentGroupService.getAll(''),
            agents: this.agentService.getAll(''),
            segs: this.segService.getAll(''),
            surveyTypes: this.sampleSurveyTypeService.getAll('').pipe(catchError(() => of(null)))
        }).pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                const validGroups = (result.agentGroups ?? []).filter(g => g?.id != null && !!g.id && g.name?.trim());
                this.agentGroups.setItems(validGroups);
                this.agents.setItems(result.agents ?? []);
                this.segs.setItems(result.segs ?? []);
                this.surveyTypes.setItems(result.surveyTypes ?? []);
                if (result.surveyTypes) {
                    this.pruneUnknownSurveyTypes();
                }
                if (this.values.selectedAgentGroupId != null) {
                    this.groupChange$.next({ groupId: this.values.selectedAgentGroupId, userDriven: false });
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['values']) {
            this.agents.syncSelection();
            this.segs.syncSelection();
            this.surveyTypes.syncSelection();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private refreshGranularityFloor(): void {
        this.granularityFloor = finestGranularityFor(
            this.values.selectedTimeRange,
            this.values.customStartDate,
            this.values.customEndDate
        );
    }

    private clampSelectedGranularity(): SampleMetricsGranularity {
        return clampGranularity(
            this.values.selectedGranularity,
            this.values.selectedTimeRange,
            this.values.customStartDate,
            this.values.customEndDate
        );
    }

    onFieldChange(field: keyof SampleCollectionMetricsFilterValues, value: unknown): void {
        this.values = { ...this.values, [field]: value };
        if (field === this.agentGroupField) {
            this.groupChange$.next({ groupId: value as string | null, userDriven: true });
        }
        this.filterChange.emit(this.values);
    }

    onTimeRangeChange(value: number): void {
        const prev = this.values.selectedTimeRange;
        this.values = { ...this.values, selectedTimeRange: value };

        if (value === SampleMetricsTimeRange.Custom) {
            this.openCustomDateDialog(prev);
            return;
        }

        this.values = {
            ...this.values,
            customStartDate: null,
            customEndDate: null,
            selectedGranularity: clampGranularity(this.values.selectedGranularity, value, null, null)
        };
        this.refreshGranularityFloor();
        this.dateRangeCtrl.syncFromStrings(null, null);
        this.filterChange.emit(this.values);
    }

    openCustomDateDialog(previousTimeRange: number): void {
        this.dateRangeCtrl.openDialog().subscribe(result => {
            if (!result) {
                this.values = { ...this.values, selectedTimeRange: previousTimeRange };
                this.filterChange.emit(this.values);
                return;
            }
            const { startIso, endIso } = this.dateRangeCtrl.applyResult(result);
            this.values = {
                ...this.values,
                selectedTimeRange: SampleMetricsTimeRange.Custom,
                customStartDate: startIso,
                customEndDate: endIso,
                selectedGranularity: clampGranularity(
                    this.values.selectedGranularity,
                    SampleMetricsTimeRange.Custom,
                    startIso,
                    endIso
                ),
            };
            this.refreshGranularityFloor();
            this.filterChange.emit(this.values);
        });
    }

    public trackById = trackById;

    private pruneUnknownSurveyTypes(): void {
        const valid = this.surveyTypes.prune(this.values.selectedSurveyTypeIds);
        if (valid) {
            this.values = { ...this.values, selectedSurveyTypeIds: valid };
            this.surveyTypes.syncSelection();
            this.filterReconcile.emit(this.values);
        }
    }

    private subscribeGroupCascade(): void {
        this.groupChange$.pipe(
            switchMap(trigger => (trigger.groupId == null
                ? of(null)
                : this.agentGroupService.getById(trigger.groupId).pipe(catchError(() => of(null)))
            ).pipe(map(group => ({ group, userDriven: trigger.userDriven })))),
            takeUntil(this.destroy$),
        ).subscribe({ next: ({ group, userDriven }) => {
            const agentIds = group?.agentIds ?? [];
            if (!agentIds.length) {
                this.agents.setBaseFilter(null);
                return;
            }
            const agentIdSet = new Set(agentIds);
            this.agents.setBaseFilter(agentIdSet);

            const validAgentIds = this.values.selectedAgentIds.filter(id => agentIdSet.has(id));
            if (validAgentIds.length !== this.values.selectedAgentIds.length) {
                this.values = { ...this.values, selectedAgentIds: validAgentIds };
                this.agents.syncSelection();
                if (userDriven) {
                    this.filterChange.emit(this.values);
                } else {
                    this.filterReconcile.emit(this.values);
                }
            }
        } });
    }
}
